import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import axios from '../../axios/axiosconf';

const SMILE_TEAL = '#17a2b8';
const SMILE_DARK = '#325e68';

/**
 * Modal de notas aclaratorias: consulta con timeline + alta de nuevas notas.
 */
function ModalNotaAclaratoria({
    show,
    onClose,
    onSaved,
    tipo,
    registroId,
    profesionalNombre = '',
    notasRegistro = [],
    puedeAgregar = false,
}) {
    const [modo, setModo] = useState('consulta');
    const [notaSeleccionadaId, setNotaSeleccionadaId] = useState(null);
    const [fecha, setFecha] = useState('');
    const [nota, setNota] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const notasOrdenadas = useMemo(() => {
        return [...(notasRegistro || [])].sort((a, b) => {
            const fa = new Date(String(a.fecha || '').replace(' ', 'T')).getTime();
            const fb = new Date(String(b.fecha || '').replace(' ', 'T')).getTime();
            return fa - fb;
        });
    }, [notasRegistro]);

    const notaSeleccionada = useMemo(
        () => notasOrdenadas.find((n) => Number(n.id) === Number(notaSeleccionadaId)) || null,
        [notasOrdenadas, notaSeleccionadaId]
    );

    const hayHistorial = notasOrdenadas.length > 0;

    const formatearAhora = () => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const h = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${y}-${m}-${day}T${h}:${min}`;
    };

    const formatearFechaDisplay = (fechaRaw) => {
        if (!fechaRaw) return '';
        const s = String(fechaRaw).replace('T', ' ');
        return s.length > 16 ? s.slice(0, 16) : s;
    };

    useEffect(() => {
        if (!show) return;

        setError('');
        setLoading(false);

        if (notasOrdenadas.length === 0) {
            setModo('crear');
            setNotaSeleccionadaId(null);
        } else {
            setModo('consulta');
            setNotaSeleccionadaId(notasOrdenadas[notasOrdenadas.length - 1].id);
        }

        setFecha(formatearAhora());
        setNota('');
    }, [show, registroId, notasRegistro]);

    const iniciarNuevaNota = () => {
        setModo('crear');
        setFecha(formatearAhora());
        setNota('');
        setError('');
    };

    const volverAConsulta = () => {
        setModo('consulta');
        setError('');
        setNota('');
        if (notasOrdenadas.length > 0) {
            setNotaSeleccionadaId(notasOrdenadas[notasOrdenadas.length - 1].id);
        }
    };

    const handleGuardar = async () => {
        if (!registroId) {
            setError('No hay registro seleccionado.');
            return;
        }
        if (!fecha) {
            setError('Indique la fecha y hora.');
            return;
        }
        if (!nota || !nota.trim()) {
            setError('Escriba la nota aclaratoria.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const fechaSql = fecha.includes('T') ? fecha.replace('T', ' ') + ':00' : fecha;
            const response = await axios.post('/nota-aclaratoria', {
                tipo,
                id_evolucion_historia: registroId,
                fecha: fechaSql,
                nota_aclaratoria: nota.trim(),
            });

            if (response.status === 201 || response.status === 200) {
                const notaGuardada = response.data?.nota;
                if (typeof onSaved === 'function') {
                    await onSaved(notaGuardada);
                }
                if (notaGuardada?.id) {
                    setNotaSeleccionadaId(notaGuardada.id);
                }
                setModo('consulta');
                setNota('');
            }
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.message ||
                'No se pudo guardar la nota aclaratoria';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <style>{`
                .modal-nota-aclaratoria-grande .modal-dialog {
                    max-width: 920px;
                    margin-left: auto;
                    margin-right: auto;
                }
                .modal-nota-aclaratoria-grande .modal-title {
                    color: ${SMILE_DARK};
                }
                .modal-nota-aclaratoria-grande .nota-historial-item {
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    background: #fff;
                    color: #495057;
                    padding: 8px 10px;
                    width: 100%;
                    text-align: left;
                    transition: border-color 0.15s, background 0.15s;
                }
                .modal-nota-aclaratoria-grande .nota-historial-item:hover {
                    border-color: ${SMILE_TEAL};
                    background: #f8f9fa;
                }
                .modal-nota-aclaratoria-grande .nota-historial-item.active {
                    border-color: ${SMILE_TEAL};
                    background: #d1ecf1;
                    color: ${SMILE_DARK};
                }
                .modal-nota-aclaratoria-grande .btn-smile {
                    background-color: ${SMILE_DARK};
                    border-color: ${SMILE_DARK};
                    color: #fff;
                }
                .modal-nota-aclaratoria-grande .btn-smile:hover:not(:disabled) {
                    background-color: #2a5058;
                    border-color: #2a5058;
                    color: #fff;
                }
                .modal-nota-aclaratoria-grande .btn-smile-outline {
                    color: ${SMILE_DARK};
                    border-color: ${SMILE_TEAL};
                    background: #fff;
                }
                .modal-nota-aclaratoria-grande .btn-smile-outline:hover:not(:disabled) {
                    background: #d1ecf1;
                    color: ${SMILE_DARK};
                    border-color: ${SMILE_TEAL};
                }
            `}</style>
            <Modal
                show={show}
                onHide={onClose}
                size="lg"
                centered
                backdrop="static"
                dialogClassName="modal-nota-aclaratoria-grande"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="h6 mb-0">
                        <i className="fas fa-comment-medical mr-2" style={{ color: SMILE_TEAL }}></i>
                        Notas Aclaratorias
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ minHeight: '300px' }}>
                    {profesionalNombre && (
                        <p className="small text-muted mb-3">
                            Profesional del registro: <strong style={{ color: SMILE_DARK }}>{profesionalNombre}</strong>
                        </p>
                    )}

                    {error && (
                        <div className="alert alert-danger py-2">{error}</div>
                    )}

                    <div className="row no-gutters">
                        {/* Contenido principal — izquierda */}
                        <div className={hayHistorial ? 'col-md-8 pr-md-3' : 'col-12'}>
                            {modo === 'consulta' && notaSeleccionada && (
                                <div>
                                    <div className="mb-2">
                                        <strong style={{ color: SMILE_DARK }}>Fecha y hora</strong>
                                        <div className="text-muted">{formatearFechaDisplay(notaSeleccionada.fecha)}</div>
                                    </div>
                                    <div>
                                        <strong style={{ color: SMILE_DARK }}>Nota Aclaratoria</strong>
                                        <div
                                            className="border rounded p-3 mt-1"
                                            style={{
                                                whiteSpace: 'pre-wrap',
                                                minHeight: '200px',
                                                background: '#f8f9fa',
                                                borderColor: '#dee2e6',
                                            }}
                                        >
                                            {notaSeleccionada.nota_aclaratoria}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {modo === 'consulta' && !notaSeleccionada && hayHistorial && (
                                <p className="text-muted mb-0">Seleccione una nota del historial.</p>
                            )}

                            {modo === 'crear' && (
                                <div>
                                    <div className="form-group">
                                        <label className="font-weight-bold" style={{ color: SMILE_DARK }}>Fecha y hora</label>
                                        <div className="input-group">
                                            <input
                                                type="datetime-local"
                                                className="form-control"
                                                value={fecha}
                                                onChange={(e) => setFecha(e.target.value)}
                                            />
                                            <div className="input-group-append">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => setFecha(formatearAhora())}
                                                >
                                                    Ahora
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-group mb-0">
                                        <label className="font-weight-bold" style={{ color: SMILE_DARK }}>Nota Aclaratoria</label>
                                        <textarea
                                            className="form-control"
                                            rows={8}
                                            value={nota}
                                            onChange={(e) => setNota(e.target.value)}
                                            placeholder="Escriba la aclaración..."
                                            maxLength={10000}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Historial — derecha (solo si hay notas) */}
                        {hayHistorial && (
                            <div className="col-md-4 pl-md-3 border-left">
                                <strong
                                    className="small text-uppercase d-block mb-2"
                                    style={{ color: SMILE_DARK, letterSpacing: '0.04em' }}
                                >
                                    Historial
                                </strong>
                                <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                    {notasOrdenadas.map((n, index) => {
                                        const activa = Number(n.id) === Number(notaSeleccionadaId) && modo === 'consulta';
                                        return (
                                            <button
                                                key={n.id}
                                                type="button"
                                                className={`nota-historial-item mb-2${activa ? ' active' : ''}`}
                                                onClick={() => {
                                                    setNotaSeleccionadaId(n.id);
                                                    setModo('consulta');
                                                    setError('');
                                                }}
                                            >
                                                <span className="d-flex align-items-start">
                                                    <i
                                                        className="fas fa-sticky-note mr-2 mt-1"
                                                        style={{ flexShrink: 0, color: SMILE_TEAL }}
                                                    ></i>
                                                    <span>
                                                        <span className="d-block font-weight-bold">Nota {index + 1}</span>
                                                        <span className="d-block small text-muted">
                                                            {formatearFechaDisplay(n.fecha)}
                                                        </span>
                                                    </span>
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <div>
                        {puedeAgregar && modo === 'consulta' && hayHistorial && (
                            <Button className="btn-smile-outline" onClick={iniciarNuevaNota} disabled={loading}>
                                <i className="fas fa-plus mr-1"></i> Nueva nota
                            </Button>
                        )}
                        {modo === 'crear' && hayHistorial && (
                            <Button variant="outline-secondary" onClick={volverAConsulta} disabled={loading}>
                                <i className="fas fa-arrow-left mr-1"></i> Volver al historial
                            </Button>
                        )}
                    </div>
                    <div>
                        <Button variant="secondary" onClick={onClose} disabled={loading} className="mr-2">
                            Cerrar
                        </Button>
                        {modo === 'crear' && puedeAgregar && (
                            <Button className="btn-smile" onClick={handleGuardar} disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm mr-1" /> Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save mr-1"></i> Guardar
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default ModalNotaAclaratoria;
